import ChangeEmailModal from '@brands/Dashboard/Sidenav/SidenavModals/ChangeEmailModal/ChangeEmailModal';
import ChangeUserStatusModal from '@brands/Dashboard/MyProfile/Modal/ChangeUserStatusModal';
import ResetPasswordModal from '@brands/Dashboard/MyProfile/Modal/ResetPasswordModal';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import { getMfaStatus } from '@brands/services/auth/getMfaStatus';
import { manageMfa } from '@brands/services/auth/manageMfa';
import { updateMe } from '@brands/services/identity/updateMe';
import { deleteCollectorUserById } from '@brands/services/identity/deleteCollectorUserById';
import { reactivateCollectorUserById } from '@brands/services/identity/reactivateCollectorUserById';
import { deleteUserById } from '@brands/services/identity/deleteUserById';
import { activateUserById } from '@brands/services/identity/activateUserById';
import { updateVerifiedEmail } from '@brands/services/identity/updateUserEmail';
import { displayErrorDetails } from '@brands/Utils/displayError';
import { FormControl, InputLabel, MenuItem, Select, Switch, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { IoLockClosedOutline, IoMailOutline, IoPhonePortraitOutline } from 'react-icons/io5';
import { toast } from 'react-toastify';

import { notificationPrefsFromUser } from '../../../../services/identity/notificationPreferences';
import { UserProfile, UserRoleName } from '../../../../services/identity/types/UserProfile';
import styles from './collectorEditUser.module.scss';

const muiSelectSx = {
  width: '100%',
  '& .MuiInputBase-root': {
    minHeight: '56px',
    borderRadius: '4px',
    backgroundColor: '#fff',
    fontFamily: 'Poppins, sans-serif',
    fontSize: '16px',
    color: 'rgba(13, 32, 53, 0.6)',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(0, 0, 0, 0.23)',
  },
  '& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--primary-color)',
    borderWidth: '1px',
  },
  '& .MuiSelect-select': {
    fontFamily: 'Poppins, sans-serif',
    fontSize: '16px',
    lineHeight: '24px',
    letterSpacing: '0.15px',
    color: 'rgba(13, 32, 53, 0.6)',
  },
  '& .MuiInputLabel-root': {
    fontFamily: 'Poppins, sans-serif',
    fontSize: '12px',
    lineHeight: '16px',
    letterSpacing: '0.15px',
    color: 'rgba(13, 32, 53, 0.6)',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: 'var(--primary-color)',
  },
  '& .MuiInputLabel-shrink': {
    backgroundColor: '#fff',
    padding: '0 4px',
  },
};

const switchSx = {
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: '#2d68a6',
  },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: '#2d68a6',
    opacity: 0.55,
  },
};

interface CollectorAccountSettingsTabProps {
  user: UserProfile | null;
  onUserSaved?: () => void | Promise<void>;
  userRole?: string;
}

const CollectorAccountSettingsTab: React.FC<CollectorAccountSettingsTabProps> = ({ user, onUserSaved, userRole = UserRoleName.Collector }) => {
  const navigate = useRoleNavigate();
  const [deactivateUserModal, setDeactivateUserModal] = useState('');
  const [activateUserModal, setActivateUserModal] = useState('');
  const [changeEmailModal, setChangeEmailModal] = useState(false);
  const [resetPasswordModal, setResetPasswordModal] = useState<{ isOpen: boolean; userState: string }>({
    isOpen: false,
    userState: '',
  });

  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaMethod, setMfaMethod] = useState<string>('EMAIL_MFA');
  const [mfaLoading, setMfaLoading] = useState(false);

  const [emailOptIn, setEmailOptIn] = useState(true);
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [notificationChannelError, setNotificationChannelError] = useState(false);

  useEffect(() => {
    if (!user) return;

    const fetchMfaStatus = async (): Promise<void> => {
      try {
        const status = await getMfaStatus(user.id);
        const enabled = status.mfa_method.length > 0;
        setMfaEnabled(enabled);
        if (enabled && status.mfa_method[0]) {
          setMfaMethod(status.mfa_method[0]);
        }
      } catch (error) {
        displayErrorDetails(error);
      }
    };
    const prefs = notificationPrefsFromUser(user);
    setEmailOptIn(prefs.email_opt_in);
    setSmsOptIn(prefs.sms_opt_in);
    setNotificationChannelError(false);

    fetchMfaStatus();
  }, [user?.id, user?.email_opt_in, user?.sms_opt_in]);

  const onSubmitChangeEmail = useCallback(
    async (email: string) => {
      try {
        if (!email) {
          throw new Error('Email is required');
        }
        if (user) {
          await updateVerifiedEmail(user.id, { email });
          await onUserSaved?.();
          toast.success('Email update request submitted.', { className: 'fs-unmask' });
        }
        setChangeEmailModal(false);
      } catch (error) {
        displayErrorDetails(error);
      }
    },
    [user, onUserSaved],
  );

  const handleMfaToggle = useCallback(
    async (checked: boolean) => {
      if (!user || mfaLoading) return;
      setMfaLoading(true);
      try {
        await manageMfa(user.id, { selected_method: checked ? mfaMethod : null });
        setMfaEnabled(checked);
      } catch (error) {
        displayErrorDetails(error);
      } finally {
        setMfaLoading(false);
      }
    },
    [user, mfaMethod, mfaLoading],
  );

  const handleMfaMethodChange = useCallback(
    async (newMethod: string) => {
      if (!user) return;
      setMfaMethod(newMethod);
      if (mfaEnabled) {
        try {
          await manageMfa(user.id, { selected_method: newMethod });
        } catch (error) {
          displayErrorDetails(error);
        }
      }
    },
    [user, mfaEnabled],
  );

  const handleNotificationToggle = useCallback(
    async (type: 'email' | 'sms') => {
      if (!user) return;
      const newEmail = type === 'email' ? !emailOptIn : emailOptIn;
      const newSms = type === 'sms' ? !smsOptIn : smsOptIn;

      if (!newEmail && !newSms) {
        setNotificationChannelError(true);
        return;
      }

      try {
        setNotificationChannelError(false);
        await updateMe(
          { email_opt_in: newEmail, sms_opt_in: newSms },
          user.id,
          userRole,
        );
        setEmailOptIn(newEmail);
        setSmsOptIn(newSms);
        await onUserSaved?.();
      } catch (error) {
        displayErrorDetails(error);
      }
    },
    [user, emailOptIn, smsOptIn, onUserSaved],
  );

  const openResetPassword = useCallback(() => {
    const status = user?.status || 'Active';
    setResetPasswordModal({ isOpen: true, userState: status });
  }, [user?.status]);

  const handleDeactivateUser = useCallback(() => {
    setDeactivateUserModal('deactivated');
  }, []);

  const handleConfirmDeactivateCollectorUser = useCallback(
    async (userId: number) => {
      if (userRole === UserRoleName.Collector) {
        await deleteCollectorUserById(userId);
      } else {
        await deleteUserById(userId, userRole);
      }
      toast.success('User has been deactivated.', { className: 'fs-unmask' });
      navigate('/users');
    },
    [navigate, userRole],
  );

  const handleActivateUser = useCallback(() => {
    setActivateUserModal('reactivate');
  }, []);

  const handleConfirmReactivateCollectorUser = useCallback(
    async (userId: number) => {
      if (userRole === UserRoleName.Collector) {
        await reactivateCollectorUserById(userId);
      } else {
        await activateUserById(userId, userRole);
      }
      toast.success('User has been activated.', { className: 'fs-unmask' });
      await onUserSaved?.();
    },
    [onUserSaved, userRole],
  );

  return (
    <div className={`${styles.form} ${styles.accountSettingsLayout}`}>
      <Typography className={`${styles.sectionTitle} ${styles.accountSettingsHeading}`} component="h2">
        Account settings
      </Typography>

      <div className={styles.settingsRow}>
        <div className={styles.settingsLabel}>
          <span className={styles.settingsLabelSmall}>Login email</span>
          <span className={styles.settingsValue}>{user?.email || '—'}</span>
        </div>
        <button type="button" className={styles.editLink} onClick={() => setChangeEmailModal(true)}>
          Edit
        </button>
      </div>

      <div className={styles.settingsRow}>
        <div className={styles.settingsLabel}>
          <span className={styles.settingsLabelSmall}>Password</span>
          <span className={styles.settingsValue}>••••••••</span>
        </div>
        <button type="button" className={styles.editLink} onClick={openResetPassword}>
          Edit
        </button>
      </div>

      <Typography className={`${styles.sectionTitle} ${styles.accountSettingsHeading}`} component="h2">
        Account security
      </Typography>
      <div className={`${styles.settingsRow} ${styles.settingsToggleRow}`}>
        <div className={styles.settingsRowLeft}>
          <IoLockClosedOutline className={styles.settingsIcon} />
          <span className={styles.settingsValue}>Multi-factor authentication</span>
        </div>
        <Switch
          checked={mfaEnabled}
          disabled={mfaLoading}
          onChange={(_e, checked) => handleMfaToggle(checked)}
          sx={switchSx}
        />
      </div>

      <FormControl fullWidth sx={muiSelectSx} className={styles.accountSettingsSelect}>
        <InputLabel id="collector-mfa-method-label">Authentication code delivery method*</InputLabel>
        <Select
          labelId="collector-mfa-method-label"
          value={mfaMethod}
          label="Authentication code delivery method*"
          onChange={(e) => handleMfaMethodChange(e.target.value)}
          disabled={!mfaEnabled}
        >
          <MenuItem value="EMAIL_MFA">Email</MenuItem>
          <MenuItem value="SMS_MFA">SMS</MenuItem>
        </Select>
      </FormControl>

      <Typography className={`${styles.sectionTitle} ${styles.accountSettingsHeading}`} component="h2">
        Notification settings
      </Typography>
      <Typography className={`${styles.sectionDescription} ${styles.accountSettingsDescription}`} component="p">
        We&apos;ll send appointment reminders and other messages from our care team to your preferred method of
        communication.
      </Typography>

      <div className={`${styles.settingsRow} ${styles.settingsToggleRow}`} >
        <div className={styles.settingsRowLeft}>
          <IoMailOutline className={styles.settingsIcon} />
          <span className={styles.settingsValue}>Email</span>
        </div>
        <Switch
          checked={emailOptIn}
          disabled={emailOptIn && !smsOptIn}
          onChange={() => handleNotificationToggle('email')}
          sx={switchSx}
        />
      </div>

      <div className={`${styles.settingsRow} ${styles.settingsToggleRow} ${styles.settingsRowNoBorder}`}>
        <div className={styles.settingsRowLeft}>
          <IoPhonePortraitOutline className={styles.settingsIcon} />
          <span className={styles.settingsValue}>SMS</span>
        </div>
        <Switch
          checked={smsOptIn}
          disabled={smsOptIn && !emailOptIn}
          onChange={() => handleNotificationToggle('sms')}
          sx={switchSx}
        />
      </div>

      {notificationChannelError && (
        <div className={styles.notificationAlert} role="alert">
          <Typography className={`fs-unmask ${styles.notificationAlertText}`} component="p">
            At least one notification method must be enabled
          </Typography>
        </div>
      )}

      {user?.status === 'Deactivated' ? (
        <button type="button" className={styles.activateUserBtn} onClick={handleActivateUser}>
          Activate user
        </button>
      ) : (
        <button type="button" className={styles.deactivateBtn} onClick={handleDeactivateUser}>
          Deactivate user
        </button>
      )}

      {deactivateUserModal && user && (
        <ChangeUserStatusModal
          setOpenModal={setDeactivateUserModal}
          disableButton={() => undefined}
          userId={user.id}
          userName={user.full_name || user.email || 'User'}
          status="deactivated"
          onConfirmDeactivate={handleConfirmDeactivateCollectorUser}
        />
      )}

      {activateUserModal && user && (
        <ChangeUserStatusModal
          setOpenModal={setActivateUserModal}
          disableButton={() => undefined}
          userId={user.id}
          userName={user.full_name || user.email || 'User'}
          status="reactivate"
          onConfirmReactivate={handleConfirmReactivateCollectorUser}
        />
      )}

      {changeEmailModal && (
        <ChangeEmailModal
          isOpen={changeEmailModal}
          onClose={() => setChangeEmailModal(false)}
          email={user?.email || ''}
          onSubmit={(email: string) => {
            void onSubmitChangeEmail(email);
          }}
        />
      )}

      {resetPasswordModal.isOpen && user && (
        <ResetPasswordModal
          setOpenModal={setResetPasswordModal}
          userId={user.id}
          userEmail={user.email}
          userName={user.full_name || user.email || 'User'}
          disableButton={() => undefined}
          isDeactivated={user.status === 'Deactivated'}
          userState={resetPasswordModal.userState}
        />
      )}
    </div>
  );
};

export default CollectorAccountSettingsTab;
